import React, {
    useEffect,
    useMemo,
    useState,
    useContext,
    useCallback,
    useRef,
} from "react";
import { useTranslation } from "react-i18next";
import "../pages.css";
import {
    MButton,
    LoadingContext,
    M_StateActions,
    MHeader,
    NavigateContext,
} from "src/M_Components";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { isFailurePayload, apiMessage } from "../../components/utils/apiResponse";
import DynamicForm from "../../components/Forms/DynamicForm";

import { STORAGE_USER } from "src/components/auth/storageKeys";

/**
 * Converts a PascalCase / UpperCamelCase property name to lowerCamelCase.
 * This is used to align UI form field names with API payload conventions.
 *
 * @param {string} s - Source string to convert.
 * @returns {string} The converted string in lowerCamelCase, or the original falsy value.
 */
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/**
 * Returns the canonical form key used to represent a foreign key field in DynamicForm.
 * This keeps the mapping consistent with DynamicForm internal logic.
 *
 * Rules:
 * - If the option has Header, Header is used.
 * - Otherwise Name is used, ensuring it ends with "Id".
 *
 * @param {object} opt - Field option metadata returned by the schema endpoint.
 * @returns {string} Foreign key form field key.
 */
const getFkFormKey = (opt) => {
    const nameKey = opt?.Name || "";
    if (opt?.Header) return opt.Header;
    return nameKey?.endsWith("Id") ? nameKey : `${nameKey}Id`;
};

/**
 * Decodes a base64url-encoded string into a UTF-8 string.
 * This is used for parsing the JWT payload segment.
 *
 * @param {string} str - base64url string to decode.
 * @returns {string} Decoded UTF-8 string or empty string on failure.
 */
const base64UrlDecode = (str) => {
    try {
        const pad = (s) => s + "=".repeat((4 - (s.length % 4)) % 4);
        const b64 = pad(str.replace(/-/g, "+").replace(/_/g, "/"));
        return decodeURIComponent(
            atob(b64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );
    } catch {
        return "";
    }
};

/**
 * Parses a JWT and returns its payload as an object.
 * If the token is missing or malformed, an empty object is returned.
 *
 * @param {string} token - JWT string stored in localStorage.
 * @returns {object} Parsed payload object or empty object.
 */
const getJwtPayload = (token) => {
    if (!token) return {};
    const parts = token.split(".");
    if (parts.length < 2) return {};
    const json = base64UrlDecode(parts[1]);
    try {
        return JSON.parse(json || "{}");
    } catch {
        return {};
    }
};

/**
 * Reads the current DefaultAccountId from the JWT payload.
 * The expected claim is "acc". If not present or not numeric, null is returned.
 *
 * @returns {number|null} Default account id from token, or null.
 */
const getCurrentDefaultAccountIdFromToken = () => {
    const token = localStorage.getItem("token");
    const payload = getJwtPayload(token);
    const acc = payload?.acc;
    const n = Number(acc);
    return Number.isFinite(n) ? n : null;
};

/**
 * Filters schema options to decide which fields should appear in Create mode.
 * This excludes:
 * - keys
 * - audit fields
 * - foreign keys (handled by other flows)
 * - Inactive (managed via Edit logic)
 * - fields marked hidden for edit
 *
 * @param {object} opt - Field option metadata.
 * @returns {boolean} True when the field should be included in Create mode.
 */
const includeInCreate_Account = (opt) => {
    if (!opt) return false;
    if (opt.IsKey) return false;
    if (
        opt.Name === "CreatedAt" ||
        opt.Name === "UpdatedAt" ||
        opt.Name === "CreatedBy" ||
        opt.Name === "UpdatedBy"
    )
        return false;
    if (opt.IsForeignKey) return false;
    if (opt.Name === "Inactive") return false;
    if (opt.IsHiddenEdit) return false;
    return true;
};

/**
 * Accounts CRUD page that supports Create/Edit/View modes and preserves per-tab state.
 * The page uses DynamicForm for schema-driven rendering and persists tab snapshots in Redux.
 *
 * Soft session refresh is performed after updating the currently active default account.
 */
export default function AccountConfigCrud({ handleTabClose, toastMessages }) {
    const dispatch = useDispatch();
    const M_State = useSelector((state) => state.StateKeyValue);
    const { t, i18n } = useTranslation();
    const ctx = useContext(LoadingContext);
    const { renameTab } = useContext(NavigateContext);

    /**
     * Reads global values used to seed this page on initial mount.
     * These are typically written by the list page before opening a tab.
     */
    const selectedTab = M_State.find((o) => o.key === "selectedTab")?.value;
    const globalOperation = M_State.find((obj) => obj.key === "operation")?.value;
    const globalLine = M_State.find((obj) => obj.key === "AccountLine")?.value;

    /**
     * Local state for the current tab id, operation mode, and selected row (line).
     * These values are restored either from global seeds or the per-tab snapshot.
     */
    const [id, setId] = useState(selectedTab);
    const [operation, setOperation] = useState(globalOperation);
    const [sessionLine, setSessionLine] = useState(globalLine);

    /** Indicates whether the current operation mode is View. */
    const isViewMode = (operation || "").toLowerCase() === "view";

    /** Current account entity data and schema options returned from the API. */
    const [accountInfo, setAccountInfo] = useState({});
    const [allOptions, setAllOptions] = useState([]);

    /**
     * Related view blocks are populated in View mode for account-related entities.
     * Each block has its own schema options and entity details.
     */
    const [contactOptions, setContactOptions] = useState([]);
    const [addressOptions, setAddressOptions] = useState([]);
    const [shipperOptions, setShipperOptions] = useState([]);
    const [apiKeyOptions, setApiKeyOptions] = useState([]);
    const [contactDetail, setContactDetail] = useState(null);
    const [addressDetail, setAddressDetail] = useState(null);
    const [shipperDetail, setShipperDetail] = useState(null);
    const [apiKeyDetail, setApiKeyDetail] = useState(null);

    /**
     * Bookkeeping flags:
     * - isFirst prevents unnecessary reloads during initial mount.
     * - accountId keeps track of the entity id for update scenarios.
     */
    const [isFirst, setIsFirst] = useState(true);
    const [accountId, setAccountId] = useState(sessionLine?.Id);

    /**
     * Reference to the DynamicForm instance to:
     * - retrieve current values for snapshot persistence
     * - trigger form submission from header buttons
     */
    const formRef = useRef(null);

    /**
     * Live refs are used to avoid stale closures in async operations.
     * This prevents handlers from using outdated state values after re-renders.
     */
    const idRef = useRef(id);
    const accountIdRef = useRef(accountId);
    const operationRef = useRef(operation);
    const sessionLineRef = useRef(sessionLine);

    useEffect(() => {
        idRef.current = id;
    }, [id]);
    useEffect(() => {
        accountIdRef.current = accountId;
    }, [accountId]);
    useEffect(() => {
        operationRef.current = operation;
    }, [operation]);
    useEffect(() => {
        sessionLineRef.current = sessionLine;
    }, [sessionLine]);

    /**
     * Per-tab token used as a guard for async requests.
     * When the tab changes, the token increments and older requests are ignored.
     */
    const tabTokenRef = useRef(0);
    useEffect(() => {
        tabTokenRef.current += 1;
    }, [id]);

    /**
     * Service instances for API communication.
     * useMemo ensures stable instances across renders for the same base URL.
     */
    const svcAccounts = useMemo(
        () => new BufferService("Accounts", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcCreate = useMemo(
        () => new BufferService("Accounts/Create", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcUpdate = useMemo(
        () => new BufferService("Accounts/Update", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    const svcContacts = useMemo(
        () => new BufferService("Contacts", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcAddresses = useMemo(
        () => new BufferService("Addresses", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcShippers = useMemo(
        () => new BufferService("Shippers", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcApiKeys = useMemo(
        () => new BufferService("ApiKeys", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    const svcMenus = useMemo(
        () => new BufferService("Menus/All", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    const svcPermissions = useMemo(
        () => new BufferService("Permissions/All", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    /**
     * Closes the current tab and removes the persisted snapshot from Redux.
     * This avoids restoring a stale tab state after navigation or reload.
     */
    const closeThisTab = useCallback(() => {
        const tabId = idRef.current;

        try {
            dispatch(M_StateActions.removeM_State({ key: tabId }));
        } catch {
            // ignored
        }

        try {
            handleTabClose?.(tabId);
        } catch {
            // ignored
        }
    }, [dispatch, handleTabClose]);

    /**
     * Updates the tab title based on operation mode and the account name.
     * A guard is used to avoid dispatch loops when the computed title is unchanged.
     */
    const lastTabTitleRef = useRef("");
    useEffect(() => {
        if (!selectedTab || !operation) return;

        const i18nKey =
            operation === "Create"
                ? "LabelNewAccount"
                : operation === "Edit"
                    ? "LabelEditAccount"
                    : "LabelViewAccount";

        const name = sessionLine?.Name ?? accountInfo?.Name ?? "";
        const base = t(i18nKey);
        const nextTitle = operation === "Create" || !name ? base : `${base} "${name}"`;

        if (lastTabTitleRef.current === nextTitle) return;
        lastTabTitleRef.current = nextTitle;

        renameTab(String(selectedTab), nextTitle);
    }, [selectedTab, operation, sessionLine?.Name, accountInfo?.Name, t, renameTab]);

    /**
     * Reads the per-tab foreign key query snapshot used by DynamicForm.
     * This restores the filter terms typed by the user inside FK dropdowns.
     */
    const fkInitialQueries = useMemo(() => {
        const tab = M_State.find((o) => o.key === id)?.value || [];
        return tab.find((x) => x.key === "dfFkQueries")?.value || {};
    }, [M_State, id]);

    /**
     * Fetches account details for a given id and updates local state.
     * Requests are guarded by the per-tab token to avoid applying stale results.
     *
     * @param {number} idToGet - Account id to fetch.
     * @returns {Promise<object|null>} Account entity or null when failed.
     */
    const fetchDetails = useCallback(
        async (idToGet) => {
            const myToken = tabTokenRef.current;

            ctx.setIsLoad(true);
            try {
                const res = await svcAccounts.getId(idToGet);
                if (isFailurePayload(res)) {
                    toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
                    return null;
                }

                if (myToken !== tabTokenRef.current) return null;

                setAccountInfo(res || {});
                setAccountId(res?.Id);
                return res || null;
            } catch (err) {
                toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
                return null;
            } finally {
                if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
            }
        },
        [ctx, svcAccounts, t, toastMessages]
    );

    /**
     * Fetches schema options and entity details for related view blocks.
     * Only runs when at least one related foreign key id is present.
     *
     * @param {object} param0 - Related ids used to load blocks.
     * @param {number|null} param0.contactId - Contact id.
     * @param {number|null} param0.addressId - Address id.
     * @param {number|null} param0.shipperId - Shipper id.
     * @param {number|null} param0.apiKeyId - ApiKey id.
     * @returns {Promise<void>}
     */
    const fetchRelatedBlocks = useCallback(
        async ({ contactId, addressId, shipperId, apiKeyId } = {}) => {
            const myToken = tabTokenRef.current;

            if (!contactId && !addressId && !shipperId && !apiKeyId) return;

            ctx.setIsLoad(true);
            try {
                const tasks = [];

                if (contactId) {
                    tasks.push(
                        (async () => {
                            const [opts, det] = await Promise.all([
                                svcContacts.options(),
                                svcContacts.getId(contactId),
                            ]);
                            if (myToken !== tabTokenRef.current) return;
                            if (!isFailurePayload(opts)) setContactOptions(opts || []);
                            if (!isFailurePayload(det)) setContactDetail(det || null);
                        })()
                    );
                }

                if (addressId) {
                    tasks.push(
                        (async () => {
                            const [opts, det] = await Promise.all([
                                svcAddresses.options(),
                                svcAddresses.getId(addressId),
                            ]);
                            if (myToken !== tabTokenRef.current) return;
                            if (!isFailurePayload(opts)) setAddressOptions(opts || []);
                            if (!isFailurePayload(det)) setAddressDetail(det || null);
                        })()
                    );
                }

                if (shipperId) {
                    tasks.push(
                        (async () => {
                            const [opts, det] = await Promise.all([
                                svcShippers.options(),
                                svcShippers.getId(shipperId),
                            ]);
                            if (myToken !== tabTokenRef.current) return;
                            if (!isFailurePayload(opts)) setShipperOptions(opts || []);
                            if (!isFailurePayload(det)) setShipperDetail(det || null);
                        })()
                    );
                }

                if (apiKeyId) {
                    tasks.push(
                        (async () => {
                            const [opts, det] = await Promise.all([
                                svcApiKeys.options(),
                                svcApiKeys.getId(apiKeyId),
                            ]);
                            if (myToken !== tabTokenRef.current) return;
                            if (!isFailurePayload(opts)) setApiKeyOptions(opts || []);
                            if (!isFailurePayload(det)) setApiKeyDetail(det || null);
                        })()
                    );
                }

                await Promise.all(tasks);
            } catch (err) {
                toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
            } finally {
                if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [svcContacts, svcAddresses, svcShippers, svcApiKeys]
    );

    /**
     * Fetches the schema options for the main DynamicForm.
     * Optionally fetches entity details when the operation is not Create.
     *
     * @param {string} op - Operation mode (Create/Edit/View).
     * @param {object|null} line - Selected row data.
     * @param {object} config - Additional options.
     * @param {boolean} config.skipDetails - When true, skips fetching entity details.
     * @returns {Promise<void>}
     */
    const fetchOptions = useCallback(
        async (op, line, { skipDetails } = { skipDetails: false }) => {
            const myToken = tabTokenRef.current;

            ctx.setIsLoad(true);
            try {
                const res = await svcAccounts.options();
                if (isFailurePayload(res) || res?.name === "AxiosError") {
                    toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
                    return;
                }

                if (myToken !== tabTokenRef.current) return;

                setAllOptions(res || []);

                const mode = op || operationRef.current || "View";
                const lineId = line?.Id ?? sessionLineRef.current?.Id;

                if (!skipDetails && mode !== "Create" && lineId) {
                    await fetchDetails(lineId);
                }
            } catch (err) {
                toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
            } finally {
                if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
            }
        },
        [ctx, fetchDetails, svcAccounts, t, toastMessages]
    );

    /**
     * Performs the initial restore from the per-tab snapshot and triggers initial fetches.
     * The snapshot acts as the source of truth when available, while API calls refresh the data.
     */
    useEffect(() => {
        const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
        const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

        const snapInfo = pick("accountInfo");
        const snapIF = pick("isFirst");
        const snapOP = pick("operation");
        const snapSL = pick("sessionLine");
        const snapSchema = pick("allOptions");

        const snapContactOptions = pick("contactOptions");
        const snapAddressOptions = pick("addressOptions");
        const snapShipperOptions = pick("shipperOptions");
        const snapApiKeyOptions = pick("apiKeyOptions");

        const snapContactDetail = pick("contactDetail");
        const snapAddressDetail = pick("addressDetail");
        const snapShipperDetail = pick("shipperDetail");
        const snapApiKeyDetail = pick("apiKeyDetail");

        if (snapInfo) setAccountInfo(snapInfo);
        if (snapIF !== undefined) setIsFirst(snapIF);
        else if (isFirst) setIsFirst(false);

        const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
        const seedLine = globalLine !== undefined ? globalLine : snapSL;

        if (seedOperation !== undefined) setOperation(seedOperation);
        if (seedLine) {
            setSessionLine(seedLine);
            if (seedLine.Id) setAccountId(seedLine.Id);
        }

        if (Array.isArray(snapSchema)) setAllOptions(snapSchema);

        if (Array.isArray(snapContactOptions)) setContactOptions(snapContactOptions);
        if (Array.isArray(snapAddressOptions)) setAddressOptions(snapAddressOptions);
        if (Array.isArray(snapShipperOptions)) setShipperOptions(snapShipperOptions);
        if (Array.isArray(snapApiKeyOptions)) setApiKeyOptions(snapApiKeyOptions);

        if (snapContactDetail) setContactDetail(snapContactDetail);
        if (snapAddressDetail) setAddressDetail(snapAddressDetail);
        if (snapShipperDetail) setShipperDetail(snapShipperDetail);
        if (snapApiKeyDetail) setApiKeyDetail(snapApiKeyDetail);

        if (globalOperation !== undefined)
            dispatch(M_StateActions.removeM_State({ key: "operation" }));
        if (globalLine !== undefined)
            dispatch(M_StateActions.removeM_State({ key: "AccountLine" }));

        fetchOptions(seedOperation, seedLine, { skipDetails: true }).finally(async () => {
            if (seedOperation === "Create") {
                ctx.setIsLoad(false);
                return;
            }

            if (seedOperation && seedLine?.Id) {
                const entity = await fetchDetails(seedLine?.Id);

                if (seedOperation === "View" && entity) {
                    await fetchRelatedBlocks({
                        contactId: entity?.DefaultContactId,
                        addressId: entity?.DefaultAddressId,
                        shipperId: entity?.DefaultShipperId,
                        apiKeyId: entity?.CargofiveApiKeyId,
                    });
                }

                ctx.setIsLoad(false);
                return;
            }

            ctx.setIsLoad(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Persists a per-tab snapshot into Redux.
     * A debounce is used to reduce dispatch frequency while typing.
     * A hash guard is used to avoid dispatching identical snapshots.
     */
    const saveSnapshotTimer = useRef(null);
    const lastSnapshotHashRef = useRef("");

    useEffect(() => {
        if (!id) return;

        const currentFormValues = formRef.current?.getValues?.() || {};
        const sanitize = (o) =>
            Object.fromEntries(Object.entries(o).filter(([, v]) => v !== ""));

        const accountInfoToPersist =
            (operation || "").toLowerCase() === "view"
                ? accountInfo
                : { ...accountInfo, ...sanitize(currentFormValues) };

        const sessionData = [
            { key: "accountInfo", value: accountInfoToPersist },
            { key: "isFirst", value: isFirst },
            { key: "operation", value: operation },
            { key: "sessionLine", value: sessionLine },
            { key: "allOptions", value: allOptions },

            { key: "contactOptions", value: contactOptions },
            { key: "addressOptions", value: addressOptions },
            { key: "shipperOptions", value: shipperOptions },
            { key: "apiKeyOptions", value: apiKeyOptions },

            { key: "contactDetail", value: contactDetail },
            { key: "addressDetail", value: addressDetail },
            { key: "shipperDetail", value: shipperDetail },
            { key: "apiKeyDetail", value: apiKeyDetail },

            { key: "accountId", value: accountId },
        ];

        const snapshotHash = JSON.stringify(sessionData);
        if (snapshotHash === lastSnapshotHashRef.current) return;
        lastSnapshotHashRef.current = snapshotHash;

        if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
        saveSnapshotTimer.current = setTimeout(() => {
            dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
        }, 120);

        return () => {
            if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
        };
    }, [
        id,
        accountInfo,
        isFirst,
        operation,
        sessionLine,
        allOptions,
        contactOptions,
        addressOptions,
        shipperOptions,
        apiKeyOptions,
        contactDetail,
        addressDetail,
        shipperDetail,
        apiKeyDetail,
        accountId,
        dispatch,
    ]);

    /**
     * Restores the snapshot for a given tab id.
     * When no snapshot exists, the data is fetched from the API using the current operation and line.
     *
     * @param {string|number} sessionID - Tab id used as the snapshot key.
     * @returns {Promise<void>}
     */
    const getSessionStorageData = useCallback(
        async (sessionID) => {
            ctx.setIsLoad(true);
            const data = M_State.find((obj) => obj.key === sessionID)?.value;

            if (data) {
                const pick = (k) => data.find((x) => x.key === k)?.value;

                setAccountInfo(pick("accountInfo") || {});
                setIsFirst(pick("isFirst") ?? false);
                setOperation(pick("operation"));
                setSessionLine(pick("sessionLine"));

                const restoredAccId =
                    pick("sessionLine")?.Id ??
                    pick("accountInfo")?.Id ??
                    (pick("accountId") ?? null);

                setAccountId(restoredAccId ?? null);

                const schema = pick("allOptions");
                if (Array.isArray(schema)) setAllOptions(schema);

                setContactOptions(pick("contactOptions") || []);
                setAddressOptions(pick("addressOptions") || []);
                setShipperOptions(pick("shipperOptions") || []);
                setApiKeyOptions(pick("apiKeyOptions") || []);
                setContactDetail(pick("contactDetail") || null);
                setAddressDetail(pick("addressDetail") || null);
                setShipperDetail(pick("shipperDetail") || null);
                setApiKeyDetail(pick("apiKeyDetail") || null);

                ctx.setIsLoad(false);
                return;
            }

            const op = operationRef.current;
            const line = sessionLineRef.current;

            await fetchOptions(op, line, { skipDetails: true }).finally(async () => {
                if (op !== "Create" && line?.Id) {
                    const entity = await fetchDetails(line?.Id);

                    if (op === "View" && entity) {
                        await fetchRelatedBlocks({
                            contactId: entity?.DefaultContactId,
                            addressId: entity?.DefaultAddressId,
                            shipperId: entity?.DefaultShipperId,
                            apiKeyId: entity?.CargofiveApiKeyId,
                        });
                    }
                }

                ctx.setIsLoad(false);
            });
        },
        [M_State, ctx, fetchDetails, fetchOptions, fetchRelatedBlocks]
    );

    /**
     * Handles tab switching by restoring the next tab snapshot.
     * This is driven by the Redux "selectedTab" value.
     */
    useEffect(() => {
        if (!selectedTab) return;
        if (selectedTab === id) return;

        setId(selectedTab);
        getSessionStorageData(selectedTab);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab]);

    /**
     * Reloads schema and updates the tab title when the language changes.
     * isFirst prevents triggering reload loops during the initial mount.
     */
    useEffect(() => {
        if (!operationRef.current || isFirst) return;

        fetchOptions(operationRef.current, sessionLineRef.current, {
            skipDetails: true,
        });

        const op = operationRef.current;
        const i18nKey =
            op === "Create"
                ? "LabelNewAccount"
                : op === "Edit"
                    ? "LabelEditAccount"
                    : "LabelViewAccount";

        const name = sessionLineRef.current?.Name ?? accountInfo?.Name ?? "";
        const base = t(i18nKey);
        const nextTitle = op === "Create" || !name ? base : `${base} "${name}"`;

        if (lastTabTitleRef.current !== nextTitle) {
            lastTabTitleRef.current = nextTitle;
            renameTab(String(selectedTab), nextTitle);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    /**
     * In View mode, loads related blocks whenever foreign keys change or the language changes.
     * This keeps the read-only view blocks consistent with the current account entity.
     */
    useEffect(() => {
        if (!isViewMode) return;

        const contactId = accountInfo?.DefaultContactId;
        const addressId = accountInfo?.DefaultAddressId;
        const shipperId = accountInfo?.DefaultShipperId;
        const apiKeyId = accountInfo?.CargofiveApiKeyId;

        if (!contactId && !addressId && !shipperId && !apiKeyId) return;

        fetchRelatedBlocks({ contactId, addressId, shipperId, apiKeyId });
    }, [
        i18n.language,
        isViewMode,
        accountInfo?.DefaultContactId,
        accountInfo?.DefaultAddressId,
        accountInfo?.DefaultShipperId,
        accountInfo?.CargofiveApiKeyId,
        fetchRelatedBlocks,
    ]);

    /**
     * Refreshes localStorage values derived from the selected default account.
     * This ensures application-level context stays consistent after updating the active account.
     *
     * @param {number} accId - Account id used to refresh local context.
     * @returns {Promise<void>}
     */
    const refreshAccountContextInLocalStorage = useCallback(
        async (accId) => {
            try {
                const acc = await svcAccounts.getId(accId);
                if (isFailurePayload(acc)) return;

                localStorage.setItem("defaultAccountName", acc?.Name || "No Account");

                let shipperName =
                    acc?.DefaultShipper?.Name || acc?.DefaultShipperName || "";

                if (!shipperName && acc?.DefaultShipperId) {
                    const det = await svcShippers.getId(acc.DefaultShipperId);
                    if (!isFailurePayload(det)) shipperName = det?.Name || shipperName;
                }

                if (acc?.DefaultShipperId != null) {
                    localStorage.setItem("defaultShipperId", String(acc.DefaultShipperId));
                } else {
                    localStorage.removeItem("defaultShipperId");
                }

                if (shipperName) {
                    localStorage.setItem("defaultShipperName", shipperName);
                } else {
                    localStorage.removeItem("defaultShipperName");
                }

                if (acc?.CargofiveApiKeyId != null) {
                    localStorage.setItem("cargofiveApiKeyId", String(acc.CargofiveApiKeyId));
                } else {
                    localStorage.removeItem("cargofiveApiKeyId");
                }
            } catch {
                // ignored
            }
        },
        [svcAccounts, svcShippers]
    );

    /**
     * Refreshes menus in localStorage.
     * Both realm-scoped and legacy keys are written to keep compatibility with older consumers.
     *
     * @returns {Promise<void>}
     */
    const refreshMenusInLocalStorage = useCallback(async () => {
        try {
            const res = await svcMenus.get();
            if (res?.name !== "AxiosError" && !isFailurePayload(res)) {
                const menusJson = JSON.stringify(res || []);
                localStorage.setItem(STORAGE_USER.menus, menusJson);
                localStorage.setItem("Menus", menusJson);
            }
        } catch {
            // ignored
        }
    }, [svcMenus]);

    /**
     * Refreshes permissions in localStorage.
     * Both realm-scoped and legacy keys are written to keep compatibility with older consumers.
     *
     * @returns {Promise<void>}
     */
    const refreshPermissionsInLocalStorage = useCallback(async () => {
        try {
            const res = await svcPermissions.get();
            if (res?.name !== "AxiosError" && !isFailurePayload(res)) {
                const permsJson = JSON.stringify(res || []);
                localStorage.setItem(STORAGE_USER.permissions, permsJson);
                localStorage.setItem("permissions", permsJson);
            }
        } catch {
            // ignored
        }
    }, [svcPermissions]);

    /**
     * Performs a soft session refresh after updating the currently active default account.
     * The current tab is closed before navigation so the UI state can be applied.
     *
     * @param {number} accId - Account id used to refresh local session context.
     * @returns {Promise<void>}
     */
    const runSelfRebootstrap = useCallback(
        async (accId) => {
            closeThisTab();

            await refreshAccountContextInLocalStorage(accId);
            await refreshMenusInLocalStorage();
            await refreshPermissionsInLocalStorage();

            setTimeout(() => {
                window.location.replace("/home");
            }, 0);
        },
        [
            closeThisTab,
            refreshAccountContextInLocalStorage,
            refreshMenusInLocalStorage,
            refreshPermissionsInLocalStorage,
        ]
    );

    /**
     * Builds the API payload from DynamicForm values based on the schema options.
     * Foreign keys are normalized to numeric ids or null, and boolean fields are coerced.
     *
     * In Edit mode, the "inactive" flag is enforced to ensure it always reaches the API payload.
     *
     * @param {object} values - Values emitted by DynamicForm.
     * @returns {object} Payload object to send to the API.
     */
    const buildPayloadFromValues = useCallback(
        (values) => {
            const options =
                Array.isArray(allOptions) && allOptions.length ? allOptions : [];

            const entries = [];

            for (const opt of options) {
                if (!opt) continue;

                const keyInValues = opt.IsForeignKey ? getFkFormKey(opt) : opt.Name;
                const payloadKey = toCamel(
                    opt.IsForeignKey ? getFkFormKey(opt) : opt.Header || opt.Name
                );

                let v;

                if (opt.IsForeignKey) {
                    const rawId = values?.[keyInValues];
                    v =
                        rawId == null || rawId === "" || Number(rawId) === 0
                            ? null
                            : Number(rawId);
                } else if (opt.Type === "boolean" || opt.Type === "bool") {
                    v = !!values?.[keyInValues];
                } else {
                    v = values?.[keyInValues];
                }

                if (v !== undefined) entries.push([payloadKey, v]);
            }

            if (
                operationRef.current === "Edit" &&
                !entries.some(([k]) => k === "inactive")
            ) {
                entries.push(["inactive", !!(values?.Inactive ?? accountInfo?.Inactive)]);
            }

            return Object.fromEntries(entries);
        },
        [allOptions, accountInfo?.Inactive]
    );

    /**
     * Handles DynamicForm submission for Create and Edit operations.
     * When the affected account matches the account currently stored in the JWT token,
     * a soft session refresh is triggered to apply the changes.
     *
     * @param {object} values - Values submitted by DynamicForm.
     * @returns {Promise<void>}
     */
    const onSubmitDynamic = useCallback(
        async (values) => {
            ctx.setIsLoad(true);

            try {
                const payload = buildPayloadFromValues(values);
                const op = (operationRef.current || "").toLowerCase();

                const currentAccId =
                    accountIdRef.current ??
                    accountInfo?.Id ??
                    sessionLineRef.current?.Id ??
                    null;

                const res =
                    op === "create"
                        ? await svcCreate.post(payload)
                        : await svcUpdate.update(currentAccId, payload);

                if (isFailurePayload(res)) {
                    toastMessages("error", "", apiMessage(res, t("ErrorSaving")));
                    return;
                }

                toastMessages(
                    "success",
                    "",
                    op === "create" ? t("AccountCreated") : t("AccountUpdated")
                );

                const createdId = res?.Id ?? res?.id ?? res?.data?.Id ?? res?.data?.id ?? null;
                const affectedId = op === "create" ? createdId : currentAccId;

                const tokenAccId = getCurrentDefaultAccountIdFromToken();

                if (
                    affectedId != null &&
                    tokenAccId != null &&
                    Number(tokenAccId) === Number(affectedId)
                ) {
                    ctx.toastMessages(
                        "success",
                        "",
                        t("RefreshingSessionToApplyChanges") ||
                        "Refreshing your session to apply these changes..."
                    );

                    await runSelfRebootstrap(affectedId);
                    return;
                }

                closeThisTab();
            } catch (err) {
                toastMessages("error", "", apiMessage(err, t("ErrorSaving")));
            } finally {
                ctx.setIsLoad(false);
            }
        },
        [
            buildPayloadFromValues,
            ctx,
            toastMessages,
            t,
            svcCreate,
            svcUpdate,
            accountInfo?.Id,
            runSelfRebootstrap,
            closeThisTab,
        ]
    );

    /**
     * Triggers DynamicForm submission programmatically.
     * This is used by the Save button located in the header.
     */
    const triggerFormSubmit = () => {
        formRef.current?.submit?.();
    };

    /**
     * Refreshes schema and entity data for the current tab.
     * In View mode, related blocks are refreshed after the main entity reload.
     *
     * @returns {Promise<void>}
     */
    const onRefresh = useCallback(async () => {
        const op = operationRef.current;
        const line = sessionLineRef.current;

        await fetchOptions(op, line);

        if (op !== "Create" && line?.Id) {
            const entity = await fetchDetails(line.Id);

            if (op === "View" && entity) {
                await fetchRelatedBlocks({
                    contactId: entity?.DefaultContactId,
                    addressId: entity?.DefaultAddressId,
                    shipperId: entity?.DefaultShipperId,
                    apiKeyId: entity?.CargofiveApiKeyId,
                });
            }
        }
    }, [fetchOptions, fetchDetails, fetchRelatedBlocks]);

    /**
     * Receives incremental value updates from DynamicForm and merges them into the per-tab snapshot.
     * Updates are ignored in View mode to reduce unnecessary Redux churn.
     *
     * FK query metadata is persisted separately in "dfFkQueries" to keep dropdown filter terms stable.
     *
     * @param {object} vals - Latest form values.
     * @param {object} meta - Optional metadata describing why the update was emitted.
     */
    const handleFormValuesChange = useCallback(
        (vals, meta) => {
            if ((operationRef.current || "").toLowerCase() === "view") return;
            if (!idRef.current) return;

            const isMetaOnly =
                meta?.cause === "fk-query" || meta?.cause === "fk-options";

            if (!isMetaOnly && (!vals || Object.keys(vals).length === 0)) return;

            const prevTabState =
                M_State.find((o) => o.key === idRef.current)?.value || [];

            const pick = (k) => prevTabState.find((x) => x.key === k)?.value;

            const mergedAccountInfo = {
                ...(pick("accountInfo") || accountInfo || {}),
                ...(vals || {}),
            };

            const updatedTabData = [
                ...prevTabState.filter(
                    (x) => x.key !== "accountInfo" && x.key !== "dfFkQueries"
                ),
                { key: "accountInfo", value: mergedAccountInfo },
                ...(meta?.cause === "fk-query"
                    ? [
                        {
                            key: "dfFkQueries",
                            value: {
                                ...(pick("dfFkQueries") || {}),
                                [meta.name]: meta.query || "",
                            },
                        },
                    ]
                    : pick("dfFkQueries") != null
                        ? [{ key: "dfFkQueries", value: pick("dfFkQueries") }]
                        : []),
            ];

            dispatch(M_StateActions.addM_State({ key: idRef.current, value: updatedTabData }));
        },
        [M_State, accountInfo, dispatch]
    );

    /**
     * Renders a read-only DynamicForm block used to display related entity details in View mode.
     * The block is only rendered when both the schema and entity details are available.
     *
     * @param {string} titleKey - Translation key for the block title.
     * @param {Array} options - Schema options for the related entity.
     * @param {object|null} details - Related entity details.
     * @returns {JSX.Element|null} The view block or null when not applicable.
     */
    const renderViewBlock = (titleKey, options, details) => {
        if (!details || !Array.isArray(options) || options.length === 0) return null;

        return (
            <div
                className={isViewMode ? "account-view-block" : undefined}
                style={{ gridColumn: "1 / -1", marginTop: "20px" }}
            >
                <DynamicForm
                    key={`df-view-${titleKey}-${details?.Id ?? "na"}`}
                    mode="view"
                    schema={options}
                    data={details}
                    onSubmit={() => { }}
                    title={t(titleKey)}
                    containerClassName="alignInputsUserForm"
                />
            </div>
        );
    };

    return (
        <>
            <MHeader
                element={
                    <>
                        <MButton
                            tooltip={t("RefreshTable")}
                            icon={<i className="pi pi-refresh iconTable"></i>}
                            onClick={onRefresh}
                            label={t("RefreshTable")}
                        />
                        {!isViewMode && (
                            <MButton
                                tooltip={t("Save")}
                                icon={<i className="pi pi-save iconTable"></i>}
                                onClick={triggerFormSubmit}
                                label={t("Save")}
                            />
                        )}
                    </>
                }
            />

            <div style={{ padding: "20px" }}>
                <div className="firstDivContainer">
                    <div className={isViewMode ? "account-view-block" : undefined}>
                        <DynamicForm
                            key={`df-main-${id}-${accountInfo?.Id ?? accountId ?? "new"}`}
                            formRef={formRef}
                            mode={(operation || "View").toLowerCase()}
                            schema={allOptions}
                            data={accountInfo}
                            onSubmit={onSubmitDynamic}
                            includeInCreate={includeInCreate_Account}
                            title={t("Account Information")}
                            containerClassName="alignInputsUserForm"
                            onValuesChange={!isViewMode ? handleFormValuesChange : null}
                            fkInitialQueries={fkInitialQueries}
                        />
                    </div>

                    {isViewMode && (
                        <>
                            {accountInfo?.DefaultAddressId &&
                                renderViewBlock(
                                    "Default Address",
                                    addressOptions,
                                    addressDetail
                                )}

                            {accountInfo?.DefaultShipperId &&
                                renderViewBlock(
                                    "Default Shipper",
                                    shipperOptions,
                                    shipperDetail
                                )}

                            {accountInfo?.DefaultContactId &&
                                renderViewBlock(
                                    "Default Contact",
                                    contactOptions,
                                    contactDetail
                                )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

AccountConfigCrud.propTypes = {
    handleTabClose: PropTypes.func,
    toastMessages: PropTypes.func,
};
